import type {Painter} from '../painter/types'
import {paintBackground} from '../lvs-foni/paint'
import {resolveSignColors} from '../lvs-foni/colors'
import type {BackgroundLayout, BgColor} from '../lvs-foni/layout'
import {paintTextRow} from '../lvs-font/paint'
import {paintArrow} from '../lvs-arrow/paint'
import type {ArrowGeometry} from '../lvs-arrow/geometry'

export interface ArrowPlacement {
    readonly geometry: ArrowGeometry
    /** Sign-frame x of the arrow's local origin (stem-tail center). */
    readonly x: number
    /** Sign-frame y of the arrow's central axis. */
    readonly y: number
}

/**
 * Paint a complete sign: background, auto-rendered text rows, optional
 * §9.20 diagonal red strike clipped to the inner rounded box, and an
 * optional arrow (sign 705). The single source of truth — driven by the
 * on-screen <SignBody>, the SVG export, and the PDF export.
 *
 * The Sign Renderer Module composes domain leaves (paintBackground,
 * paintTextRow, paintArrow) onto a Painter. Caller supplies a
 * precomputed layout, the input parameters that select colors and
 * overlays, and an optional arrow placement.
 */
export function paintSign(
    p: Painter,
    layout: BackgroundLayout,
    bgColor: BgColor,
    strike: boolean,
    arrow?: ArrowPlacement
): void {
    paintBackground(p, layout, bgColor)

    const {innerBox, rowPlacements, cornerRadius, contrastBorder, blackEdge} = layout
    const colors = resolveSignColors(bgColor)
    const bgOffset = contrastBorder + blackEdge

    for (const row of rowPlacements) {
        paintTextRow(
            p,
            colors.text,
            row.row.text,
            row.row.variant,
            innerBox.x + row.x,
            innerBox.y + row.baselineY,
            row.row.h
        )
    }

    if (arrow) {
        paintArrow(p, arrow.geometry, colors.text, arrow.x, arrow.y)
    }

    const strikeWidth = 0.4 * layout.maxRowHeight
    if (strike && strikeWidth > 0) {
        const r = Math.max(0, cornerRadius - bgOffset)
        p.withClipRoundedRect(innerBox.x, innerBox.y, innerBox.width, innerBox.height, r, () => {
            p.strokeLine(
                'red',
                strikeWidth,
                innerBox.x,
                innerBox.y + innerBox.height,
                innerBox.x + innerBox.width,
                innerBox.y
            )
        })
    }
}
